/**
 * useChatStream — SSE realtime per la chat WhatsApp-style (utenti + admin).
 * Apre uno stream verso ``/api/chat/stream?token=`` e inoltra ogni evento
 * JSON a ``onEvent``. Riconnessione automatica con backoff (come le notifiche
 * admin). Funziona per QUALSIASI utente loggato (non solo admin).
 */
import { useEffect, useRef } from "react";
import { BACKEND_URL } from "../lib/api";

export function useChatStream({ enabled = true, onEvent, onStatus }) {
  const onEventRef = useRef(onEvent);
  const onStatusRef = useRef(onStatus);
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);
  useEffect(() => { onStatusRef.current = onStatus; }, [onStatus]);
  const esRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryAttemptRef = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const token = localStorage.getItem("ah_token");
      if (!token) return;
      if (esRef.current) {
        try { esRef.current.close(); } catch { /* ignore */ }
        esRef.current = null;
      }
      const url = `${BACKEND_URL}/api/chat/stream?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url, { withCredentials: false });
      esRef.current = es;

      es.onopen = () => { retryAttemptRef.current = 0; onStatusRef.current?.("open"); };
      es.onmessage = (ev) => {
        if (!ev?.data) return;
        try {
          const payload = JSON.parse(ev.data);
          if (payload.type === "ping" || payload.type === "connected") return;
          onEventRef.current?.(payload);
        } catch { /* ignore malformed */ }
      };
      es.onerror = () => {
        onStatusRef.current?.("error");
        try { es.close(); } catch { /* ignore */ }
        esRef.current = null;
        if (cancelled) return;
        const attempt = ++retryAttemptRef.current;
        const delay = Math.min(30000, 1500 * 2 ** Math.min(attempt - 1, 5));
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      cancelled = true;
      if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
      if (esRef.current) { try { esRef.current.close(); } catch { /* ignore */ } esRef.current = null; }
      onStatusRef.current?.("closed");
    };
  }, [enabled]);
}
